import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { 
  MessageSquare, Search, Plus, Send, Paperclip, Image,
  Phone, Video, MoreVertical, CheckCheck, Clock, Shield,
  Brain, Star, Archive, Trash2, AlertCircle
} from 'lucide-react';

const BehavioralHealthMessages = () => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const conversations = [
    {
      id: 1,
      client: 'Sarah Johnson',
      lastMessage: 'Thank you for the resources you shared. I found them very helpful.',
      timestamp: '2 hours ago',
      unread: 2,
      status: 'online'
    },
    {
      id: 2,
      client: 'Michael Chen',
      lastMessage: 'Can we reschedule our session to Thursday instead?',
      timestamp: '5 hours ago',
      unread: 1,
      status: 'offline'
    },
    {
      id: 3,
      client: 'Emily Rodriguez',
      lastMessage: 'I completed the homework exercises you assigned.',
      timestamp: 'Yesterday',
      unread: 0,
      status: 'offline'
    },
    {
      id: 4,
      client: 'David Thompson',
      lastMessage: 'Looking forward to our first session tomorrow!',
      timestamp: 'Yesterday',
      unread: 0,
      status: 'online'
    },
  ];

  const messages = [
    { id: 1, sender: 'client', text: 'Hi, I wanted to follow up on what we discussed in our last session.', time: '10:30 AM', read: true },
    { id: 2, sender: 'therapist', text: 'Of course! How have you been feeling since we last spoke?', time: '10:32 AM', read: true },
    { id: 3, sender: 'client', text: 'I\'ve been practicing the breathing exercises you taught me. They\'ve been really helpful during stressful moments.', time: '10:35 AM', read: true },
    { id: 4, sender: 'therapist', text: 'That\'s wonderful to hear! Consistency is key. Have you noticed any patterns in when you need to use them most?', time: '10:38 AM', read: true },
    { id: 5, sender: 'client', text: 'Yes, mostly in the mornings before work and sometimes during lunch. The workplace stress has been better overall.', time: '10:42 AM', read: true },
    { id: 6, sender: 'client', text: 'Thank you for the resources you shared. I found them very helpful.', time: '11:15 AM', read: false },
  ];

  const sendMessage = () => {
    if (newMessage.trim()) {
      // Add message logic here
      setNewMessage('');
    }
  };

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-64px)] flex bg-gray-50">
        {/* Conversations List */}
        <div className="w-96 bg-white border-r border-gray-200 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-orange-500" />
                Messages
              </h2>
              <button className="p-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors">
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <div className="relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          {/* HIPAA Notice */}
          <div className="px-4 py-2 bg-green-50 border-b border-green-100">
            <div className="flex items-center gap-2 text-green-700 text-sm">
              <Shield className="w-4 h-4" />
              <span>All messages are HIPAA compliant and encrypted</span>
            </div>
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                onClick={() => setSelectedConversation(conv)}
                className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${
                  selectedConversation?.id === conv.id ? 'bg-orange-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="relative">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
                      {conv.client.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                      conv.status === 'online' ? 'bg-green-500' : 'bg-gray-400'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">{conv.client}</h4>
                      <span className="text-xs text-gray-500">{conv.timestamp}</span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{conv.lastMessage}</p>
                  </div>
                  {conv.unread > 0 && (
                    <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                      {conv.unread}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Message Area */}
        {selectedConversation ? (
          <div className="flex-1 flex flex-col">
            {/* Chat Header */}
            <div className="p-4 bg-white border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
                    {selectedConversation.client.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${
                    selectedConversation.status === 'online' ? 'bg-green-500' : 'bg-gray-400'
                  }`} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{selectedConversation.client}</h3>
                  <p className="text-sm text-gray-500">
                    {selectedConversation.status === 'online' ? 'Online' : 'Last seen recently'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Phone className="w-5 h-5 text-gray-600" />
                </button>
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Video className="w-5 h-5 text-gray-600" />
                </button>
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <MoreVertical className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
              <div className="text-center">
                <span className="px-3 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">Today</span>
              </div>
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.sender === 'therapist' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] px-4 py-3 rounded-2xl ${
                    msg.sender === 'therapist' 
                      ? 'bg-orange-500 text-white rounded-br-md' 
                      : 'bg-white text-gray-800 shadow rounded-bl-md'
                  }`}>
                    <p className="text-sm">{msg.text}</p>
                    <div className={`flex items-center justify-end gap-1 mt-1 ${
                      msg.sender === 'therapist' ? 'text-orange-200' : 'text-gray-400'
                    }`}>
                      <span className="text-xs">{msg.time}</span>
                      {msg.sender === 'therapist' && (
                        <CheckCheck className={`w-4 h-4 ${msg.read ? 'text-white' : 'text-orange-200'}`} />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Message Input */}
            <div className="p-4 bg-white border-t border-gray-200">
              <div className="flex items-center gap-3">
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Paperclip className="w-5 h-5 text-gray-500" />
                </button>
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Image className="w-5 h-5 text-gray-500" />
                </button>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button 
                  onClick={sendMessage}
                  className="p-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Select a conversation</h3>
              <p className="text-gray-500">Choose a client to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BehavioralHealthMessages;
